import React from 'react';

interface OrderSummaryProps {
  planName: string;
  billingCadence: 'monthly' | 'annual';
  amount: number;
  currency?: string;
  monthlyEquivalent?: number;
}

export default function OrderSummary({
  planName,
  billingCadence,
  amount,
  currency = 'USD',
  monthlyEquivalent,
}: OrderSummaryProps) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const formattedAmount = formatter.format(amount);
  const formattedMonthly = monthlyEquivalent ? formatter.format(monthlyEquivalent) : null;

  return (
    <div className="bg-white/5 border border-white/20 rounded-lg p-3 sm:p-6 mb-4 sm:mb-6">
      <div className="space-y-2 sm:space-y-3">
        {/* Plan Name and Billing Cadence */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="text-white font-medium text-sm sm:text-base">{planName}</div>
            <div className="text-white/60 text-xs sm:text-sm mt-0.5 sm:mt-1">
              {billingCadence === 'annual' ? 'Billed annually' : 'Billed monthly'}
            </div>
          </div>
          {billingCadence === 'annual' && formattedMonthly && (
            <div className="text-white/70 text-xs sm:text-sm ml-4 text-right whitespace-nowrap">
              <span className="text-white/50">x12 </span>
              <span>{formattedMonthly}</span>
              <span className="text-white/50"> per month</span>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="h-px bg-white/10 my-2 sm:my-3" />

        {/* Subtotal (only show if different from total) */}
        {billingCadence === 'annual' && (
          <>
            <div className="flex items-center justify-between">
              <div className="text-white/70 text-xs sm:text-sm">Subtotal</div>
              <div className="text-white/70 text-xs sm:text-sm">{formattedAmount}</div>
            </div>
            <div className="h-px bg-white/10 my-2 sm:my-3" />
          </>
        )}

        {/* Total */}
        <div className="flex items-center justify-between pt-0.5 sm:pt-1">
          <div className="text-white font-semibold text-sm sm:text-base">Total Due Today</div>
          <div className="text-white font-bold text-base sm:text-lg">{formattedAmount}</div>
        </div>
      </div>
    </div>
  );
}

